import { closestCenter, DndContext, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Button, Paper, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import DialogAdaptive from '../../../components/common/DialogAdaptive';
import DialogContentAdaptive from '../../../components/common/DialogContentAdaptive';
import StyledCardTitleBar from '../../../components/common/StyledCardTitleBar';
import StyledDialogActions from '../../../components/common/StyledDialogActions';
import type { PpvMenuItem } from '../../../hooks/useMenu';

const sortMenuItemsStable = (items: PpvMenuItem[]): PpvMenuItem[] =>
    [...items].sort((a, b) => {
        const d = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
        if (d !== 0) return d;
        return String(a.id).localeCompare(String(b.id));
    });

const SortableMenuRow = ({ item }: { item: PpvMenuItem }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging, setActivatorNodeRef } = useSortable({
        id: item.id,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.55 : 1,
    };

    return (
        <Paper
            ref={setNodeRef}
            style={style}
            variant="outlined"
            sx={{
                px: 1.5,
                py: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                borderRadius: 1,
            }}
        >
            <Box
                ref={setActivatorNodeRef}
                component="span"
                {...attributes}
                {...listeners}
                sx={{
                    display: 'inline-flex',
                    cursor: 'grab',
                    color: 'text.secondary',
                    touchAction: 'none',
                    '&:active': { cursor: 'grabbing' },
                }}
            >
                <DragIndicatorIcon fontSize="small" />
            </Box>
            <Stack direction="column" spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" color="primary" fontWeight={600} noWrap>
                    #{item.command}
                </Typography>
                <Typography variant="body2" color="text.primary" noWrap title={item.name}>
                    {item.name}
                </Typography>
            </Stack>
        </Paper>
    );
};

interface MenuItemReorderModalProps {
    open: boolean;
    onClose: () => void;
    items: PpvMenuItem[];
    updateMenuItem: (menuItemId: string, payload: { sortOrder: number }) => Promise<PpvMenuItem | null>;
    onSaved: () => Promise<void>;
}

const MenuItemReorderModal = ({ open, onClose, items, updateMenuItem, onSaved }: MenuItemReorderModalProps) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [ordered, setOrdered] = useState<PpvMenuItem[]>([]);
    const [saving, setSaving] = useState(false);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    useEffect(() => {
        if (!open) {
            return;
        }
        setOrdered(sortMenuItemsStable(items));
    }, [open, items]);

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) {
            return;
        }
        const oldIndex = ordered.findIndex(i => i.id === active.id);
        const newIndex = ordered.findIndex(i => i.id === over.id);
        if (oldIndex < 0 || newIndex < 0) {
            return;
        }
        setOrdered(prev => arrayMove(prev, oldIndex, newIndex));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            for (let i = 0; i < ordered.length; i += 1) {
                const item = ordered[i];
                // eslint-disable-next-line no-await-in-loop
                const updated = await updateMenuItem(item.id, { sortOrder: i });
                if (!updated) {
                    enqueueSnackbar(`Could not update order for #${item.command}`, { variant: 'error' });
                    return;
                }
            }
            await onSaved();
            enqueueSnackbar('Menu order updated', { variant: 'success' });
            onClose();
        } finally {
            setSaving(false);
        }
    };

    return (
        <DialogAdaptive
            open={open}
            onClose={(_e, _reason) => {
                if (saving) {
                    return;
                }
                onClose();
            }}
            fullScreen={fullScreen}
        >
            <StyledCardTitleBar title="Menu order" />
            <DialogContentAdaptive>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Drag items to set the order fans see in <strong>#menu</strong>. Only the command and name are shown here.
                </Typography>
                {ordered.length === 0 ? (
                    <Typography variant="body2">No menu items to reorder.</Typography>
                ) : (
                    <Box
                        sx={{
                            height: fullScreen ? 'calc(100vh - 220px)' : 'min(56vh, 520px)',
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            overscrollBehavior: 'contain',
                            pr: 0.5,
                        }}
                    >
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd} autoScroll={false}>
                            <SortableContext items={ordered.map(i => i.id)} strategy={verticalListSortingStrategy}>
                                <Stack spacing={1}>
                                    {ordered.map(item => (
                                        <SortableMenuRow key={item.id} item={item} />
                                    ))}
                                </Stack>
                            </SortableContext>
                        </DndContext>
                    </Box>
                )}
            </DialogContentAdaptive>
            <StyledDialogActions sx={{ px: 2, py: 1.5 }}>
                <Button variant="outlined" color="inherit" onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <LoadingButton variant="contained" color="primary" onClick={handleSave} loading={saving} disabled={ordered.length === 0}>
                    Save order
                </LoadingButton>
            </StyledDialogActions>
        </DialogAdaptive>
    );
};

export default MenuItemReorderModal;
